import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Tag,
  Tooltip,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { InternationalShipmentStatusData, InternationalShipmentStatusCreate, InternationalShipmentStatusUpdate } from '../types';
import { internationalShipmentStatusAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Search } = Input;

const InternationalShipmentStatuses: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('international-shipment-statuses');
  const [statuses, setStatuses] = useState<InternationalShipmentStatusData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStatus, setEditingStatus] = useState<InternationalShipmentStatusData | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  useEffect(() => {
    loadStatuses();
  }, [showActiveOnly, searchText]);

  const loadStatuses = async () => {
    setLoading(true);
    try {
      const response = await internationalShipmentStatusAPI.getInternationalShipmentStatuses({
        is_active: showActiveOnly ? true : undefined,
        search: searchText || undefined
      });
      setStatuses(response);
    } catch (error) {
      message.error('Errore nel caricamento degli stati spedizioni internazionali');
      console.error('Error loading international shipment statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Stati usati dal programma per la sequenza delle spedizioni: codice fisso,
  // sempre attivi e non eliminabili (il backend lo impone comunque)
  const SYSTEM_STATUS_CODES = ['in_preparation', 'shipped', 'in_transit', 'arrived', 'completed'];
  const isSystemStatus = (code?: string) => SYSTEM_STATUS_CODES.includes((code || '').toLowerCase());
  const editingIsSystem = !!editingStatus && isSystemStatus(editingStatus.code);

  const handleCreate = () => {
    setEditingStatus(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, sort_order: 0 });
    setIsModalVisible(true);
  };

  const handleEdit = (record: InternationalShipmentStatusData) => {
    setEditingStatus(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const res: any = await internationalShipmentStatusAPI.deleteInternationalShipmentStatus(id);
      message.success(res?.message || 'Stato spedizione eliminato');
      loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nell\'eliminazione dello stato spedizione');
      console.error('Error deleting international shipment status:', error);
    }
  };

  const handleSubmit = async (values: InternationalShipmentStatusCreate | InternationalShipmentStatusUpdate) => {
    try {
      if (editingStatus) {
        await internationalShipmentStatusAPI.updateInternationalShipmentStatus(editingStatus.id, values);
        message.success('Stato spedizione internazionale aggiornato con successo');
      } else {
        await internationalShipmentStatusAPI.createInternationalShipmentStatus(values);
        message.success('Stato spedizione internazionale creato con successo');
      }
      setIsModalVisible(false);
      form.resetFields();
      loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nel salvataggio dello stato spedizione');
      console.error('Error saving international shipment status:', error);
    }
  };

  const columns = [
    {
      title: 'Ordine',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      render: (order: number) => (
        <Tag color="blue">{order}</Tag>
      ),
    },
    {
      title: 'Codice',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (text: string, record: InternationalShipmentStatusData) => (
        <Space>
          <code>{text}</code>
          {isSystemStatus(text) && (
            <Tooltip title="Stato usato dal programma: codice fisso, sempre attivo, non eliminabile">
              <Tag color="blue">Sistema</Tag>
            </Tooltip>
          )}
          {!record.is_active && (
            <Tag color="red" size="small">Disattivato</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Descrizione',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Stato',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Attivo' : 'Disattivo'}
        </Tag>
      ),
    },
    {
      title: 'Creato',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('it-IT'),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 150,
      render: (_, record: InternationalShipmentStatusData) => (
        <Space size="small">
          <Tooltip title="Modifica">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          {!isSystemStatus(record.code) && (
            <Popconfirm
              title="Sei sicuro di voler eliminare questo stato?"
              description="Questa azione non può essere annullata."
              onConfirm={() => handleDelete(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Tooltip title="Elimina">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="Gestione Stati Spedizioni Internazionali" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Cerca per codice o descrizione"
              allowClear
              onSearch={setSearchText}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <span>Solo attivi:</span>
              <Switch
                checked={showActiveOnly}
                onChange={setShowActiveOnly}
              />
            </Space>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Space style={{ float: 'right' }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadStatuses}
                title="Ricarica"
              />
              {canEdit && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  Nuovo Stato
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <Table
          dataSource={statuses}
          columns={canEdit ? columns : columns.filter((c) => c.key !== 'actions')}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} di ${total} stati`,
          }}
        />
      </Card>

      <Modal
        title={editingStatus ? 'Modifica Stato Spedizione' : 'Nuovo Stato Spedizione'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true, sort_order: 0 }}
        >
          <Form.Item
            name="code"
            label="Codice"
            rules={[
              { required: true, message: 'Inserire il codice dello stato' },
              { max: 50, message: 'Il codice non può superare 50 caratteri' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: 'Il codice può contenere solo lettere, numeri e underscore' }
            ]}
          >
            <Input
              className="no-uppercase-modal"
              placeholder="es: in_preparation"
              disabled={editingIsSystem}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descrizione"
            rules={[
              { required: true, message: 'Inserire la descrizione dello stato' },
              { max: 200, message: 'La descrizione non può superare 200 caratteri' }
            ]}
          >
            <Input className="no-uppercase-modal" placeholder="es: In Preparazione" />
          </Form.Item>

          <Form.Item
            name="sort_order"
            label="Ordine di Visualizzazione"
            rules={[
              { required: true, message: 'Inserire l\'ordine di visualizzazione' }
            ]}
          >
            <InputNumber
              min={0}
              max={100}
              placeholder="0"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Stato"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Attivo"
              unCheckedChildren="Disattivo"
              disabled={editingIsSystem}
            />
          </Form.Item>
          {editingIsSystem && (
            <div style={{ marginTop: -12, marginBottom: 12, color: '#888', fontSize: 12 }}>
              Stato di sistema: si possono modificare solo descrizione e ordine di visualizzazione.
            </div>
          )}

          <Form.Item style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Annulla
              </Button>
              <Button type="primary" htmlType="submit">
                {editingStatus ? 'Aggiorna' : 'Crea'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InternationalShipmentStatuses;